/**
 * StatusSelect Component
 * Status dropdown with colored options
 * Pre-configured for common status types (pass/fail/warning/etc.)
 */

import React from 'react';
import { Select, Tag } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { colors } from '../../theme/designTokens';

const { Option } = Select;

/**
 * Pre-defined status options
 */
const defaultStatusOptions = [
  { value: 'all', label: 'All Statuses', color: colors.neutral[600], icon: null },
  { value: 'pass', label: 'Pass', color: colors.status.pass, icon: <CheckCircleOutlined /> },
  { value: 'fail', label: 'Fail', color: colors.status.fail, icon: <CloseCircleOutlined /> },
  { value: 'warning', label: 'Warning', color: colors.status.warning, icon: <ExclamationCircleOutlined /> },
  { value: 'running', label: 'Running', color: colors.status.running, icon: <ClockCircleOutlined /> },
  { value: 'pending', label: 'Pending', color: colors.status.unknown, icon: <ClockCircleOutlined /> },
  { value: 'unknown', label: 'Unknown', color: colors.neutral[400], icon: <QuestionCircleOutlined /> },
];

/**
 * StatusSelect Component
 *
 * @param {string} value - Selected status
 * @param {function} onChange - Change handler
 * @param {Array} options - Custom status options (default: defaultStatusOptions)
 * @param {string} placeholder - Placeholder text
 * @param {boolean} allowClear - Allow clear selection (default: true)
 * @param {string} size - Select size ('small' | 'default' | 'large')
 * @param {Object} style - Additional styles
 * @param {Object} rest - Additional Select props
 */
function StatusSelect({
  value,
  onChange,
  options = defaultStatusOptions,
  placeholder = 'Select status',
  allowClear = true,
  size = 'default',
  style = {},
  ...rest
}) {
  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      allowClear={allowClear}
      size={size}
      style={{ width: 150, ...style }}
      {...rest}
    >
      {options.map((option) => (
        <Option key={option.value} value={option.value}>
          <span style={{ color: option.color }}>
            {option.icon && <span style={{ marginRight: 8 }}>{option.icon}</span>}
            {option.label}
          </span>
        </Option>
      ))}
    </Select>
  );
}

export default StatusSelect;
export { defaultStatusOptions };
